import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { canonicalWorkloadLevel, workloadTone } from "../lib/workloadPresentation";

const managerWorkloadLabel = (manager, t) => {
  const level = canonicalWorkloadLevel(manager?.workload);
  return `${t("workloadLabel")} ${level ? t(`workload_${level}`) : t("notAvailable")}`;
};

function ManagerLine({ manager, t }) {
  const workloadLabel = managerWorkloadLabel(manager, t);
  return <span className="!flex !overflow-visible min-w-0 w-full items-center gap-3 text-left" aria-hidden="true">
    <span className="min-w-0 flex-1 truncate">{manager.name || "—"}</span>
    <span
      className="block h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ "--care-manager-workload-tone": workloadTone(manager.workload), backgroundColor: "var(--care-manager-workload-tone)" }}
      title={workloadLabel}
      data-testid={`care-manager-workload-${manager.id}`}
      data-workload-level={canonicalWorkloadLevel(manager.workload) || "unknown"}
    />
  </span>;
}

export default function CareManagerSelect({ managers, value, onChange, t, testId, required = false }) {
  const selected = managers.find((manager) => manager.id === value);
  const selectedLabel = selected
    ? `${selected.name || "—"}, ${managerWorkloadLabel(selected, t)}`
    : t("primaryCareManager");

  return <Select value={value || ""} onValueChange={onChange} required={required}>
    <SelectTrigger
      data-testid={testId}
      className="pc-input h-auto min-h-[48px] min-w-0 px-4 py-3 text-[15px] shadow-none"
      style={{ background: "var(--pc-charcoal)", borderColor: "var(--pc-border)", color: "var(--pc-text)" }}
      aria-label={selectedLabel}
    >
      {selected ? <ManagerLine manager={selected} t={t} /> : <SelectValue placeholder="—" />}
    </SelectTrigger>
    <SelectContent
      position="popper"
      viewportClassName="!h-auto max-h-64"
      className="min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] rounded-xl p-1"
      style={{ background: "var(--pc-charcoal)", border: "1px solid var(--pc-border)", color: "var(--pc-text)" }}
    >
      {managers.map((manager) => {
        const accessibleLabel = `${manager.name || "—"}, ${managerWorkloadLabel(manager, t)}`;
        return <SelectItem
          key={manager.id}
          value={manager.id}
          textValue={accessibleLabel}
          aria-label={accessibleLabel}
          showIndicator={false}
          itemTextAsChild
          data-testid={`care-manager-option-${manager.id}`}
          className="min-w-0 px-3 py-2.5 data-[highlighted]:bg-[var(--pc-cyan-dim)] data-[highlighted]:text-[var(--pc-text)] data-[state=checked]:bg-[var(--pc-cyan-dim)]"
        >
          <ManagerLine manager={manager} t={t} />
        </SelectItem>;
      })}
    </SelectContent>
  </Select>;
}

export { managerWorkloadLabel };
